<template>
  <ExLoader v-if="!dashboardReady" text="Loading..." />
  <YourDashboardNoAssignments
    v-else-if="!hasCurrentPositionAssignment"
    :readiness="readiness"
    :last_month_readiness="last_month_readiness"
    :readiness-map="readinessMap"
    :chart-data="chartData"
    :responsibilities-count="responsibilitiesCount"
    :badges-count="badgesCount"
    average-readiness-title="Readiness"
    map-title="Readiness Map"
    chart-title="Readiness over time"
    responsibilities-card-title="Responsibilities"
    badges-card-title="Badges"
  />
  <div v-else class="employee-readiness-dashboard">
    <div class="employee-readiness-dashboard__map">
      <YourReadinessMap
        :novice_value="readinessMap.novice"
        :apprentice_value="readinessMap.apprentice"
        :proficient_value="readinessMap.professional"
        :coach_value="readinessMap.coach"
        :architect_value="readinessMap.master"
        :countShow="false"
        mapTitle="Readiness Map"
      />
    </div>

    <div class="employee-readiness-dashboard__main-row">
      <div class="employee-readiness-dashboard__chart">
        <ReadinessEvolutionChart
          :today="chartData.today"
          :today_month_minus_1="chartData.today_month_minus_1"
          :today_month_minus_2="chartData.today_month_minus_2"
          :today_month_minus_3="chartData.today_month_minus_3"
          :today_month_minus_4="chartData.today_month_minus_4"
          :today_month_minus_5="chartData.today_month_minus_5"
          :today_month_minus_6="chartData.today_month_minus_6"
          chartTitle="Readiness over time"
        />
      </div>
      <div class="employee-readiness-dashboard__cards">
        <YourCountOfAssignedResponsibilitiesDashboardCard
          :assigned_responsibilities_with_apprentice_level="responsibilitiesCount.apprentice"
          :assigned_responsibilities_with_professional_level="responsibilitiesCount.professional"
          :assigned_responsibilities_with_coach_level="responsibilitiesCount.coach"
          :assigned_responsibilities_with_master_level="responsibilitiesCount.master"
          :assigned_responsibilities_with_novice_level="responsibilitiesCount.novice"
          cardTitle="Responsibilities"
        />
        <YourCountOfCurrentBadgesDashboardCard
          :apprentice_badge_count="badgesCount.apprentice"
          :professional_badge_count="badgesCount.professional"
          :coach_badge_count="badgesCount.coach"
          :master_badge_count="badgesCount.master"
          cardTitle="Badges"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router/composables";
import YourReadinessMap from "@components/YourReadinessMap/YourReadinessMap.vue";
import ReadinessEvolutionChart from "@components/ReadinessEvolutionChart/ReadinessEvolutionChart.vue";
import YourCountOfAssignedResponsibilitiesDashboardCard from "@components/YourCountOfAssignedResponsibilitiesDashboardCard/YourCountOfAssignedResponsibilitiesDashboardCard.vue";
import YourCountOfCurrentBadgesDashboardCard from "@components/YourCountOfCurrentBadgesDashboardCard/YourCountOfCurrentBadgesDashboardCard.vue";
import YourDashboardNoAssignments from "@components/YourDashboardNoAssignments/YourDashboardNoAssignments.vue";
import ExLoader from "@components/common/ExLoader.vue";
import {
  getEmployeeById,
  getAuthUserProficiencyLevel,
  getAllEmployeeBadges,
  getReadinessEvolution,
} from "@/api";
import { employeeHasCurrentPositionAssignment } from "@/utils/employeePositionAssignment";
import { deriveDashboardBadgeCountsFromResults } from "@/utils/employeeBadgeListUtils";

const route = useRoute();

const readiness = ref(null);
const last_month_readiness = ref(null);
const readinessMap = ref({
  novice: 0,
  apprentice: 0,
  professional: 0,
  coach: 0,
  master: 0,
});
const chartData = ref({
  today: null,
  today_month_minus_1: null,
  today_month_minus_2: null,
  today_month_minus_3: null,
  today_month_minus_4: null,
  today_month_minus_5: null,
  today_month_minus_6: null,
});
const responsibilitiesCount = ref({
  novice: 0,
  apprentice: 0,
  professional: 0,
  coach: 0,
  master: 0,
});
const badgesCount = ref({ apprentice: 0, professional: 0, coach: 0, master: 0 });
const dashboardReady = ref(false);
const hasCurrentPositionAssignment = ref(true);

onMounted(async () => {
  const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
  const empId = route?.params?.id || route?.query?.employeeId;

  if (!accountID || !empId) {
    hasCurrentPositionAssignment.value = false;
    dashboardReady.value = true;
    return;
  }

  try {
    const [employeeRes, proficiencyRes, badgesRes, weekRes, monthRes, quarterRes] =
      await Promise.allSettled([
        getEmployeeById(accountID, empId),
        getAuthUserProficiencyLevel(accountID, empId),
        getAllEmployeeBadges(accountID, empId, { status: "current" }),
        getReadinessEvolution(accountID, empId, "week"),
        getReadinessEvolution(accountID, empId, "month"),
        getReadinessEvolution(accountID, empId, "quarter"),
      ]);

    if (employeeRes.status === "fulfilled") {
      const emp = employeeRes.value.data;
      hasCurrentPositionAssignment.value = employeeHasCurrentPositionAssignment(emp);
      readiness.value = emp.current_readiness ?? null;
      last_month_readiness.value = emp.last_month_readiness ?? null;
    } else {
      hasCurrentPositionAssignment.value = true;
    }

    const weeks = weekRes.status === "fulfilled" ? weekRes.value.data.periods : Array(7).fill(null);
    const months = monthRes.status === "fulfilled" ? monthRes.value.data.periods : Array(7).fill(null);
    const quarters = quarterRes.status === "fulfilled" ? quarterRes.value.data.periods : Array(7).fill(null);
    const keys = [
      "today_month_minus_6",
      "today_month_minus_5",
      "today_month_minus_4",
      "today_month_minus_3",
      "today_month_minus_2",
      "today_month_minus_1",
      "today",
    ];
    const merged = {};
    keys.forEach((key, i) => {
      merged[key] = {
        weeks: weeks[i]?.value ?? null,
        months: months[i]?.value ?? null,
        quarters: quarters[i]?.value ?? null,
      };
    });
    chartData.value = merged;

    if (proficiencyRes.status === "fulfilled") {
      const p = proficiencyRes.value.data;
      readinessMap.value = {
        novice: p.novice || 0,
        apprentice: p.apprentice || 0,
        professional: p.professional || 0,
        coach: p.expert || 0,
        master: p.master || 0,
      };
      responsibilitiesCount.value = {
        novice: p.novice || 0,
        apprentice: p.apprentice || 0,
        professional: p.professional || 0,
        coach: p.expert || 0,
        master: p.master || 0,
      };
    }

    if (badgesRes.status === "fulfilled") {
      const currentBadges = badgesRes.value?.results || [];
      badgesCount.value = deriveDashboardBadgeCountsFromResults(currentBadges);
    }
  } finally {
    dashboardReady.value = true;
  }
});
</script>

<style scoped>
.employee-readiness-dashboard {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background-color: transparent;
}

.employee-readiness-dashboard__map {
  width: 100%;
}

.employee-readiness-dashboard__main-row {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.employee-readiness-dashboard__chart {
  flex: 1;
  min-width: 0;
}

.employee-readiness-dashboard__cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 0 0 330px;
}
</style>
